/**
 * The browser-wide coordinator for session state shared by CRPM tabs.
 *
 * Network requests deliberately happen outside the lock. The lock covers only the short marker
 * snapshots and mutations around them, so tabs cannot derive and write the same next generation.
 * The generation and the server's expected-session-instance guard still fence requests that overlap
 * a later login or logout. Browsers without Web Locks, and lock requests the browser refuses,
 * retain that fencing without client-side serialization. A logout departure is the exception: it
 * holds this lock until the document unloads so a login cannot start between the decision and the
 * navigation. A nondestructive departure for Resume also releases after a timeout.
 */
import {
  automaticLogoutSupersededSince as readAutomaticLogoutSupersededSince,
  beginSessionPing as captureSessionPing,
  checkExpiredCountdown as readExpiredCountdown,
  claimSessionTransition,
  claimedExplicitLogoutSupersededSince,
  explicitLogoutSupersededSince,
  finishSessionTransition as completeSessionTransition,
  hasNewSession as readHasNewSession,
  logoutContinuationSuperseded as readLogoutContinuationSuperseded,
  markSessionTransition,
  noteSessionPing as recordSessionPing,
  readSessionMarker,
  type AutomaticLogoutSuperseded,
  type SessionMarker,
  type ExpiredCountdown,
  type ObservingPage,
  type PingRecord,
  type SessionPing,
  type TransitionCause,
} from './sessionMarker'

export type { AutomaticLogoutSuperseded } from './sessionMarker'
export { forgetTabSession, readSessionMarker, readTabSession } from './sessionMarker'

const SESSION_LOCK_NAME = 'crpm.auth.session-marker'

/**
 * How long a nondestructive departure for Resume may hold the session lock after starting
 * navigation. The next document has normally unloaded by then. Waiting longer would block login on
 * every other tab, because this lock is the only one those logins take.
 */
const RESUME_DEPARTURE_LOCK_TIMEOUT_MS = 10_000

function lockManager(): LockManager | undefined {
  return typeof navigator !== 'undefined' ? navigator.locks : undefined
}

async function withSessionLock<T>(action: () => T | Promise<T>): Promise<T> {
  const locks = lockManager()
  if (!locks) {
    return action()
  }
  let ran = false
  try {
    return await locks.request(SESSION_LOCK_NAME, { mode: 'exclusive' }, () => {
      ran = true
      return action()
    })
  } catch (error) {
    // Once the action has run, never run it again: a failure inside it, or while the browser
    // settled the request, reaches the caller rather than applying a mutation twice.
    if (ran) {
      throw error
    }
    // The browser refused the lock request itself, for example in a document that's no longer fully
    // active. Run unserialized, as without Web Locks: the generation and the server's session guard
    // still fence requests. Failing instead would leave a countdown that never logs out, or a
    // Resume button that does nothing.
    return action()
  }
}

/**
 * Captures the generation and the session the page observed before a request starts; see the
 * marker's `beginSessionPing` for `page`.
 */
export function beginSessionPing(page?: ObservingPage): Promise<SessionPing> {
  return withSessionLock(() => captureSessionPing(page))
}

/** Records a ping response if the captured generation still owns it. */
export function noteSessionPing(
  ping: SessionPing,
  sessionInstanceId: string | null | undefined,
  expirySeconds: number,
  now = Date.now(),
): Promise<PingRecord> {
  return withSessionLock(() => recordSessionPing(ping, sessionInstanceId, expirySeconds, now))
}

/** Starts a login or unconditional logout and invalidates older work. */
export function startSessionTransition(cause: TransitionCause): Promise<number> {
  return withSessionLock(() => markSessionTransition(cause))
}

/** Claims an automatic logout only if the tab still knows the browser's current session. */
export function claimAutomaticLogout(
  sessionInstanceId: string | undefined,
): Promise<number | undefined> {
  return withSessionLock(() => claimSessionTransition(sessionInstanceId))
}

export type UnnamedExplicitLogout =
  | { kind: 'claimed'; generation: number }
  | { kind: 'superseded'; supersededBy: AutomaticLogoutSuperseded }

/**
 * Starts an explicit logout that never learned its session, unless a login completed while the
 * request was in flight. The read and the write share the lock, so a login cannot land between them.
 */
export function startExplicitLogoutUnlessLogin(
  observed: SessionMarker | undefined,
): Promise<UnnamedExplicitLogout> {
  return withSessionLock(() => {
    const supersededBy = explicitLogoutSupersededSince(observed)
    if (supersededBy !== undefined) {
      return { kind: 'superseded', supersededBy }
    }
    return { kind: 'claimed', generation: markSessionTransition('explicitLogout') }
  })
}

/** Why an automatic logout must stop after its claim, if it must. */
export function automaticLogoutSupersededSince(
  generation: number,
  sessionInstanceId: string | undefined,
): Promise<AutomaticLogoutSuperseded | undefined> {
  return withSessionLock(() => readAutomaticLogoutSupersededSince(generation, sessionInstanceId))
}

/** Completes an unconditional logout transition. */
export function finishSessionTransition(generation: number): Promise<void> {
  return withSessionLock(() => completeSessionTransition(generation))
}

/**
 * Atomically finishes an explicit logout and announces it only if no newer login or explicit
 * logout owns the browser. The callback runs while the lock is held, so a cooperating login cannot
 * land between the final check and the broadcast.
 */
export function finishExplicitLogout(
  generation: number,
  sessionInstanceId: string | undefined,
  announce: () => void,
): Promise<AutomaticLogoutSuperseded | undefined> {
  return withSessionLock(() => {
    const supersededBy = claimedExplicitLogoutSupersededSince(generation, sessionInstanceId)
    if (supersededBy === undefined) {
      completeSessionTransition(generation)
      announce()
    }
    return supersededBy
  })
}

/**
 * Atomically rechecks and completes an automatic logout. A later login or explicit logout is left
 * untouched; otherwise, pings that began while the server request was in flight are invalidated.
 */
export function finishAutomaticLogout(
  generation: number,
  sessionInstanceId: string | undefined,
): Promise<AutomaticLogoutSuperseded | undefined> {
  return withSessionLock(() => {
    const supersededBy = readAutomaticLogoutSupersededSince(generation, sessionInstanceId)
    if (supersededBy === undefined) {
      completeSessionTransition(generation)
    }
    return supersededBy
  })
}

/** Whether another tab has begun or completed a login since this page observed its session. */
export function hasNewSession(sessionInstanceId: string | undefined): Promise<boolean> {
  return withSessionLock(() => readHasNewSession(sessionInstanceId))
}

/**
 * Finishes a logout continuation only if no login began or completed while its server response was
 * delayed. The Cognito departure and the Resume departure both run while the lock is held.
 * `location.replace` returns before the next document loads, so releasing when it returns would let
 * a waiting login write its transition in that gap.
 *
 * Cognito logout is destructive, so its hold has no timeout: neither stopping a load nor waiting a
 * fixed duration can prove that Cognito will not complete an already-received request afterward.
 * The browser releases a still-held Web Lock when this document is discarded. A Resume departure
 * is nondestructive, so it may time out rather than block login on every other tab indefinitely. In
 * either case, `pagehide` releases the hold, including when the browser is putting the page in the
 * back/forward cache.
 */
export function finishLogoutContinuation(
  sessionInstanceId: string | undefined,
  continueLogout: () => void,
  goToResume: () => void,
): Promise<boolean> {
  return withSessionLock(() => {
    const superseded = readLogoutContinuationSuperseded(sessionInstanceId)
    const hold = armDepartureHold(superseded ? RESUME_DEPARTURE_LOCK_TIMEOUT_MS : undefined)
    try {
      if (superseded) {
        goToResume()
      } else {
        continueLogout()
      }
      return hold.done.then(() => superseded)
    } catch (error) {
      hold.cancel()
      throw error
    }
  })
}

/**
 * Leaves for Resume while holding the session lock, for a continuation the server has already
 * refused. There is no second marker decision: the refusal is the decision, and the navigation has
 * to be the same step so it cannot overlap a login's marker write.
 */
export function holdResumeDeparture(depart: () => void): Promise<void> {
  return withSessionLock(() => {
    const hold = armDepartureHold(RESUME_DEPARTURE_LOCK_TIMEOUT_MS)
    try {
      depart()
      return hold.done
    } catch (error) {
      hold.cancel()
      throw error
    }
  })
}

/** Listens for `pagehide` before the departure, so a synchronous unload is not missed. */
function armDepartureHold(timeoutMs: number | undefined): {
  done: Promise<void>
  cancel: () => void
} {
  let cancel = () => {}
  const done = new Promise<void>((resolve, reject) => {
    let settled = false
    let timer: number | undefined
    const settle = (failure?: Error) => {
      if (settled) {
        return
      }
      settled = true
      if (timer !== undefined) {
        window.clearTimeout(timer)
      }
      window.removeEventListener('pagehide', finish)
      if (failure) {
        reject(failure)
      } else {
        resolve()
      }
    }
    function finish() {
      settle()
    }
    cancel = finish
    window.addEventListener('pagehide', finish)
    if (timeoutMs !== undefined) {
      timer = window.setTimeout(() => {
        window.stop()
        settle(new Error('Navigation to the Resume page did not complete.'))
      }, timeoutMs)
    }
  })
  return { done, cancel }
}

/** Decides what an expired per-tab countdown should do from one serialized marker snapshot. */
export function checkExpiredCountdown(
  sessionInstanceId: string | undefined,
  deadline: number | undefined,
  now = Date.now(),
): Promise<ExpiredCountdown> {
  return withSessionLock(() => readExpiredCountdown(sessionInstanceId, deadline, now))
}

export type ProtectedSessionRefresh =
  | { kind: 'current'; ping: SessionPing }
  | { kind: 'newSession' }
  | { kind: 'superseded' }

export type ProtectedSessionRefreshStatus = ProtectedSessionRefresh['kind']

function protectedSessionRefreshStatus(
  ping: SessionPing,
  observedSessionInstanceId = ping.sessionInstanceId,
): ProtectedSessionRefreshStatus {
  if (readHasNewSession(observedSessionInstanceId)) {
    return 'newSession'
  }
  const current = readSessionMarker()
  if (current?.kind === 'transition') {
    return 'superseded'
  }
  if (ping.generation === 0) {
    // Another tab's first ping may have established generation 1 for this same session.
    return current === undefined || current.generation === 1 ? 'current' : 'superseded'
  }
  return current?.generation === ping.generation ? 'current' : 'superseded'
}

/**
 * Takes the snapshot used before a protected token refresh and decides, under the same lock,
 * whether this tab still belongs to the browser's current session.
 */
export function beginProtectedSessionRefresh(): Promise<ProtectedSessionRefresh> {
  return withSessionLock(() => {
    const ping = captureSessionPing()
    const kind = protectedSessionRefreshStatus(ping)
    return kind === 'current' ? { kind, ping } : { kind }
  })
}

/**
 * Checks the same fence after a protected request, before its response is applied to the page.
 */
export function checkProtectedSessionRefresh(
  ping: SessionPing,
  observedSessionInstanceId = ping.sessionInstanceId,
): Promise<ProtectedSessionRefreshStatus> {
  return withSessionLock(() => protectedSessionRefreshStatus(ping, observedSessionInstanceId))
}
